<template>
	<div class="">
		<form @submit.stop.prevent class="w-full">
			<div class="w-full text-sm text-ihh-form-input-entry space-y-2">
				<!-- Name -->
				<div class="w-full md:flex md:justify-between md:space-x-2">
					<div class="w-full mb-2 md:mb-0">
						<div class="relative w-full my-1 rounded border ihh-form-border bg-ihh-form-background transition-all focus-within:border-ihh-dark-salmon">
							<input
								id="shipping-firstName"
								v-model="newAddress.firstName"
								type="text"
								class="peer w-full border-none bg-transparent px-2.5 pt-6 pb-2 text-sm ihh-form-input-entry focus:outline-none"
								placeholder=" "
								autocomplete="given-name"
							/>
							<label
								for="shipping-firstName"
								class="absolute left-2.5 top-2 text-xs ihh-form-input-label transition-all peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:text-sm peer-focus:top-2 peer-focus:-translate-y-0 peer-focus:text-xs"
							>
								First Name
							</label>
						</div>
					</div>
					<div class="w-full">
						<div class="relative w-full my-1 rounded border ihh-form-border bg-ihh-form-background transition-all focus-within:border-ihh-dark-salmon">
							<input
								id="shipping-lastName"
								v-model="newAddress.lastName"
								type="text"
								class="peer w-full border-none bg-transparent px-2.5 pt-6 pb-2 text-sm ihh-form-input-entry focus:outline-none"
								placeholder=" "
								autocomplete="family-name"
							/>
							<label
								for="shipping-lastName"
								class="absolute left-2.5 top-2 text-xs ihh-form-input-label transition-all peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:text-sm peer-focus:top-2 peer-focus:-translate-y-0 peer-focus:text-xs"
							>
								Last Name
							</label>
						</div>
					</div>
				</div>

				<!-- Company -->
				<div class="relative w-full my-1 rounded border ihh-form-border bg-ihh-form-background transition-all focus-within:border-ihh-dark-salmon">
					<input
						id="shipping-company"
						v-model="newAddress.company"
						type="text"
						class="peer w-full border-none bg-transparent px-2.5 pt-6 pb-2 text-sm ihh-form-input-entry focus:outline-none"
						placeholder=" "
						autocomplete="organization"
					/>
					<label
						for="shipping-company"
						class="absolute left-2.5 top-2 text-xs ihh-form-input-label transition-all peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:text-sm peer-focus:top-2 peer-focus:-translate-y-0 peer-focus:text-xs"
					>
						<span>Company</span><span class="text-ihh-form-input-optional ml-1">(Optional)</span>
					</label>
				</div>

				<!-- Address -->
				<div class="relative w-full my-1 rounded border ihh-form-border bg-ihh-form-background transition-all focus-within:border-ihh-dark-salmon">
					<input
						id="shipping-autocomplete"
						v-model="newAddress.address1"
						type="text"
						class="peer w-full border-none bg-transparent px-2.5 pt-6 pb-2 text-sm ihh-form-input-entry focus:outline-none transition-all duration-300"
						:class="{ 'ring-ihh-dark-salmon border-ihh-dark-salmon ring-1 rounded-md': tempFocusFields.includes('address1') }"
						placeholder=" "
						ref="addressInput"
						autocomplete="shipping street-address"
					/>
					<label
						for="shipping-autocomplete"
						class="absolute left-2.5 top-2 text-xs ihh-form-input-label transition-all peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:text-sm peer-focus:top-2 peer-focus:-translate-y-0 peer-focus:text-xs"
					>
						Address
					</label>
				</div>

				<!-- Apartment/Suite/Building -->
				<div class="relative w-full my-1 rounded border ihh-form-border bg-ihh-form-background transition-all focus-within:border-ihh-dark-salmon">
					<input
						id="shipping-address2"
						v-model="newAddress.address2"
						type="text"
						class="peer w-full border-none bg-transparent px-2.5 pt-6 pb-2 text-sm ihh-form-input-entry focus:outline-none"
						placeholder=" "
						autocomplete="address-line2"
					/>
					<label
						for="shipping-address2"
						class="absolute left-2.5 top-2 text-xs ihh-form-input-label transition-all peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:text-sm peer-focus:top-2 peer-focus:-translate-y-0 peer-focus:text-xs"
					>
						<span>Apartment/Suite/Building</span><span class="text-ihh-form-input-optional ml-1">(Optional)</span>
					</label>
				</div>

				<!-- City & State -->
				<div class="w-full md:flex md:justify-between md:space-x-2">
					<div class="w-full mb-2 md:mb-0">
						<div class="relative w-full my-1 rounded border ihh-form-border bg-ihh-form-background transition-all focus-within:border-ihh-dark-salmon">
							<!-- Military City Dropdown -->
							<select
								v-if="isMilitaryAddress"
								id="shipping-city"
								v-model="newAddress.city"
								class="appearance-none peer w-full border-none bg-transparent px-2.5 pt-6 pb-2 text-sm ihh-form-input-entry focus:outline-none transition-all duration-300"
								:class="{ 'ring-ihh-dark-salmon border-ihh-dark-salmon ring-1 rounded-md': tempFocusFields.includes('city') }"
							>
								<option value="" disabled>Select military city</option>
								<option v-for="city in militaryCities" :key="city.code" :value="city.code">{{ city.code }} - {{ city.name }}</option>
							</select>
							<!-- Regular City Input -->
							<input
								v-else
								id="shipping-city"
								v-model="newAddress.city"
								type="text"
								class="peer w-full border-none bg-transparent px-2.5 pt-6 pb-2 text-sm ihh-form-input-entry focus:outline-none transition-all duration-300"
								:class="{ 'ring-ihh-dark-salmon border-ihh-dark-salmon ring-1 rounded-md': tempFocusFields.includes('city') }"
								placeholder=" "
								autocomplete="address-level2"
							/>
							<label
								for="shipping-city"
								class="pointer-events-none absolute left-2.5 top-2 text-xs ihh-form-input-label transition-all peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:text-sm peer-focus:top-2 peer-focus:-translate-y-0 peer-focus:text-xs"
							>
								{{ isMilitaryAddress ? 'Military City' : 'City' }}
							</label>
						</div>
					</div>
					<div class="w-full">
						<div class="relative w-full my-1 rounded border ihh-form-border bg-ihh-form-background transition-all focus-within:border-ihh-dark-salmon">
							<select
								id="shipping-stateOrProvince"
								v-model="newAddress.stateOrProvince"
								type="text"
								class="appearance-none peer w-full border-none bg-transparent px-2.5 pt-6 pb-2 text-sm ihh-form-input-entry focus:outline-none transition-all duration-300"
								:class="{ 'ring-ihh-dark-salmon border-ihh-dark-salmon ring-1 rounded-md': tempFocusFields.includes('stateOrProvince') }"
								placeholder=" "
								autocomplete="address-level1"
							>
								<option value="" disabled>{{ isMilitaryAddress ? 'Select military state' : 'Select a state' }}</option>
								<template v-if="isMilitaryAddress">
									<option v-for="state in militaryStates" :key="state.code" :value="state.code">{{ state.code }} - {{ state.name }}</option>
								</template>
								<template v-else>
									<option v-for="state in states" :key="state.name" :value="state.name">{{ state.name }}</option>
								</template>
							</select>
							<label
								for="shipping-stateOrProvince"
								class="pointer-events-none absolute left-2.5 top-2 text-xs ihh-form-input-label transition-all peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:text-sm peer-focus:top-2 peer-focus:-translate-y-0 peer-focus:text-xs"
							>
								{{ isMilitaryAddress ? 'Military State' : 'State/Province' }}
							</label>
						</div>
					</div>
				</div>

				<!-- Postal Code -->
				<div class="relative w-full my-1 rounded border ihh-form-border bg-ihh-form-background transition-all focus-within:border-ihh-dark-salmon">
					<input
						id="shipping-postalCode"
						v-model="newAddress.postalCode"
						type="string"
						class="peer w-full border-none bg-transparent px-2.5 pt-6 pb-2 text-sm ihh-form-input-entry focus:outline-none transition-all duration-300"
						:class="{ 'ring-ihh-dark-salmon border-ihh-dark-salmon ring-1 rounded-md': tempFocusFields.includes('postalCode') }"
						placeholder=" "
						autocomplete="postal-code"
					/>
					<label
						for="shipping-postalCode"
						class="absolute left-2.5 top-2 text-xs ihh-form-input-label transition-all peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:text-sm peer-focus:top-2 peer-focus:-translate-y-0 peer-focus:text-xs"
					>
						Postal Code
					</label>
				</div>

				<!-- Country -->
				<div class="relative w-full my-1 rounded border ihh-form-border bg-ihh-form-background transition-all focus-within:border-ihh-dark-salmon">
					<select
						@change="fetchStates"
						v-model="newAddress.countryCode"
						id="shipping-countryCode"
						type="text"
						class="appearance-none peer w-full border-none bg-transparent px-2.5 pt-6 pb-2 text-sm ihh-form-input-entry focus:outline-none transition-all duration-300"
						:class="{ 'ring-ihh-dark-salmon border-ihh-dark-salmon ring-1 rounded-md': tempFocusFields.includes('countryCode') }"
						placeholder=" "
						autocomplete="country"
					>
						<option value="" disabled>Select Country</option>
						<option v-for="country in countries" :key="country.isoCode" :value="country.isoCode">{{ country.name }}</option>
					</select>
					<label
						for="shipping-countryCode"
						class="absolute left-2.5 top-2 text-xs ihh-form-input-label transition-all peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:text-sm peer-focus:top-2 peer-focus:-translate-y-0 peer-focus:text-xs"
					>
						Country
					</label>
				</div>

				<!-- Due Date -->
				<div class="w-full flex space-x-2 justify-between md:justify-start">
					<div class="w-full md:w-1/3">
						<div class="relative w-full my-1 rounded border ihh-form-border bg-ihh-form-background transition-all focus-within:border-ihh-dark-salmon">
							<input
								id="shipping-dueDate"
								v-model="newAddress.dueDate"
								type="date"
								placeholder=" "
								class="peer w-full border-none bg-transparent px-2.5 pt-6 pb-2 text-sm ihh-form-input-entry focus:outline-none"
							/>
							<label
								for="shipping-dueDate"
								class="absolute left-2.5 top-2 text-xs ihh-form-input-label transition-all peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:text-sm peer-focus:top-2 peer-focus:-translate-y-0 peer-focus:text-xs"
							>
								<span>Due Date</span><span class="text-ihh-form-input-optional ml-1">(Optional)</span>
							</label>
						</div>
					</div>
					<div class="hidden md:flex items-center">
						<button type="button" @click="resetDueDate">
							<svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="#453c50">
								<path
									d="M444-144q-107-14-179.5-94.5T192-430q0-61 23-113.5t63-91.5l51 51q-30 29-47.5 69T264-430q0 81 51.5 140T444-217v73Zm72 0v-73q77-13 128.5-72.5T696-430q0-90-63-153t-153-63h-7l46 46-51 50-132-132 132-132 51 51-45 45h6q120 0 204 84t84 204q0 111-72.5 192T516-144Z"
								/>
							</svg>
						</button>
					</div>
				</div>

				<!-- Phone number -->
				<div class="relative w-full my-1 rounded border ihh-form-border bg-ihh-form-background transition-all focus-within:border-ihh-dark-salmon">
					<input
						@input="formatPhoneNumber($event)"
						@change="formatPhoneNumber($event)"
						id="shipping-phoneNumber"
						:value="formattedPhoneNumber"
						type="tel"
						class="peer w-full border-none bg-transparent px-2.5 pt-6 pb-2 text-sm ihh-form-input-entry focus:outline-none"
						placeholder=" "
						autocomplete="tel"
					/>
					<label
						for="shipping-phoneNumber"
						class="absolute left-2.5 top-2 text-xs ihh-form-input-label transition-all peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:text-sm peer-focus:top-2 peer-focus:-translate-y-0 peer-focus:text-xs"
					>
						Phone Number
					</label>
				</div>

				<div class="pt-2"></div>

				<!-- Questions section -->
				<section class="px-4 py-1 bg-gray-100 rounded">
					<!-- Billing Address Toggle -->
					<div class="m-auto my-3 items-center flex w-full">
						<div class="flex items-center m-auto w-full pr-4">
							<label class="custom-checkbox relative inline-block overflow-hidden w-4 h-4 bg-transparent rounded border border-gray-300 cursor-pointer mr-2">
								<input type="checkbox" v-model="localBillingMatchesShipping" class="absolute opacity-0 w-0 h-0" />
								<span class="checkmark absolute inset-0 flex items-center justify-center hidden text-ihh-white/90 text-lg">✓</span>
							</label>
							<div class="text-ihh-slate-gray text-sm items-center text-start">My billing address is the same as my shipping address.</div>
						</div>
					</div>

					<!-- HC Provider -->
					<!-- <div class="w-full">
                         <div class="text-ihh-slate-gray text-sm items-center text-start">Are you a healthcare provider?</div>
                         <div class="m-auto mt-1 items-center flex w-full">
                             <div class="flex items-center m-auto w-full pr-4">
                                 <label
                                     for="hcProvider"
                                     class="custom-checkbox relative inline-block overflow-hidden w-4 h-4 bg-transparent rounded border border-gray-300 cursor-pointer mr-2"
                                 >
                                     <input id="hcProvider" type="checkbox" v-model="newAddress.hcProvider" class="absolute opacity-0 w-0 h-0" />
                                     <span class="checkmark absolute inset-0 flex items-center justify-center hidden text-ihh-white/90 text-lg">✓</span>
                                 </label>
                                 <div class="text-ihh-slate-gray text-sm items-center text-start">Yes <span class="text-ihh-form-input-optional ml-1">(Optional)</span></div>
                             </div>
                         </div>
                     </div> -->

					<div class="m-auto my-3 items-center flex w-full">
						<div class="flex items-center m-auto w-full pr-4">
							<label class="custom-checkbox relative inline-block overflow-hidden w-4 h-4 bg-transparent rounded border border-gray-300 cursor-pointer mr-2">
								<input type="checkbox" v-model="newAddress.hcProvider" class="absolute opacity-0 w-0 h-0" />
								<span class="checkmark absolute inset-0 flex items-center justify-center hidden text-ihh-white/90 text-lg">✓</span>
							</label>
							<div class="text-ihh-slate-gray text-sm items-center text-start">Are you a healthcare provider?</div>
						</div>
					</div>

					<!-- Military Address Toggle -->
					<div class="m-auto my-2 items-center flex w-full">
						<div class="flex items-center m-auto w-full">
							<label class="custom-checkbox relative inline-block overflow-hidden w-4 h-4 bg-transparent rounded border border-gray-300 cursor-pointer mr-2">
								<input type="checkbox" v-model="isMilitaryAddress" class="absolute opacity-0 w-0 h-0" />
								<span class="checkmark absolute inset-0 flex items-center justify-center hidden text-ihh-white/90 text-lg">✓</span>
							</label>
							<div class="text-ihh-slate-gray text-sm items-center text-start">Shipping to a US Military Address (APO/FPO/DPO)?</div>
						</div>
					</div>

					<!-- <div v-if="store?.user?.id" class="m-auto items-center flex w-full">
                         <div class="flex items-center m-auto w-full pr-4">
                             <label class="custom-checkbox relative inline-block overflow-hidden w-4 h-4 bg-transparent rounded border border-gray-300 cursor-pointer mr-2">
                                 <input type="checkbox" v-model="saveNewAddress" class="absolute opacity-0 w-0 h-0" />
                                 <span class="checkmark absolute inset-0 flex items-center justify-center hidden text-ihh-white/90 text-lg">✓</span>
                             </label>
                             <div class="text-ihh-slate-gray text-sm items-center text-start">Save this address in my address book.</div>
                         </div>
                     </div> -->
				</section>

				<div class="w-full flex justify-center md:justify-start">
					<button
						@click="saveAddress"
						class="flex w-full justify-center md:max-w-40 text-cendter mt-4 uppercase bg-ihh-dark-salmon hover:bg-ihh-salmon ease-in-out duration-100 transition text-ihh-midnight-gray font-semibold tracking-wide py-3 px-4 text-sm rounded items-center"
					>
						{{ submitButtonText }}
					</button>
				</div>
			</div>
		</form>
	</div>
</template>

<script>
import { Country, State } from 'country-state-city'
import { debounce } from 'lodash'
import Swal from 'sweetalert2'
import { useCheckoutStore } from '../../stores/store.js'
import { isGoogleMapsReady } from '../../helpers/googleMaps.js'
import { parseGooglePlaceComponents } from '../../helpers/parseGooglePlaceComponents.js'
import { devLog } from '../../helpers/devLog.js'

export default {
	inject: ['store'],
	emits: ['newAddressCompleted', 'saveNewAddress', 'shippingAddressSynced'],
	props: {
		enableAutoComplete: {
			type: Boolean,
			default: false,
		},
		showAddressForm: {
			type: Boolean,
			default: false,
		},
		/** True after the customer has submitted this step once (Continue); not when debounced Pinia sync validates fields */
		addressStepCommitted: {
			type: Boolean,
			default: false,
		},
	},
	name: 'ShippingAddressForm',
	data() {
		return {
			countries: [],
			states: [],
			creatingAddress: true,
			updatingAddress: false,
			editingAddressId: null,
			tempFocusFields: [],
			newAddress: {
				address1: '',
				address2: '',
				city: '',
				company: '',
				firstName: '',
				lastName: '',
				phone: '',
				postalCode: '',
				stateOrProvince: '',
				countryCode: 'US',
				hcProvider: false,
				dueDate: null,
				isMilitary: false,
			},
			addresses: [],
			required: ['firstName', 'lastName', 'address1', 'city', 'stateOrProvince', 'postalCode', 'countryCode', 'phone'],
			formattedPhoneNumber: '',
			missingFields: [],
			addressValidated: false,
			saveNewAddress: false,
			localBillingMatchesShipping: false,
			checkout: useCheckoutStore(),
			isMilitaryAddress: false,
			autocompleteInstance: null,
			militaryCities: [
				{ code: 'APO', name: 'Army Post Office' },
				{ code: 'FPO', name: 'Fleet Post Office' },
				{ code: 'DPO', name: 'Diplomatic Post Office' },
			],
			militaryStates: [
				{ code: 'AA', name: 'Armed Forces Americas' },
				{ code: 'AE', name: 'Armed Forces Europe' },
				{ code: 'AP', name: 'Armed Forces Pacific' },
			],
			suppressDebouncedSync: false,
		}
	},
	computed: {
		submitButtonText() {
			return this.addressStepCommitted ? 'Update' : 'Continue'
		},
	},
	created() {
		this.fetchCountries()
		this.states = State.getStatesOfCountry('US')
		this.debouncedSyncShippingToStore = debounce(() => this.syncShippingToStore(), 600)
	},
	beforeUnmount() {
		this.debouncedSyncShippingToStore?.cancel?.()
	},

	mounted() {
		// Only set billingMatchesShipping to true on first load when billing address is empty
		if (!this.checkout.billingAddressValidated.value) {
			// This is the first time loading the shipping address and billing is empty
			this.localBillingMatchesShipping = true
		}

		if (this.checkout.shippingAddressValidated.value) {
			this.copyStoreAddressToLocal()
		}

		setTimeout(() => {
			this.syncPhoneFromDOM('shipping-phoneNumber')
		}, 300)
	},

	methods: {
		saveAddress() {
			this.syncPhoneFromDOM('shipping-phoneNumber')
			this.validateAddress()
			if (!this.addressValidated) {
				Swal.fire({
					icon: 'warning',
					title: 'Missing Information',
					html: this.missingFields.length
						? `<div class="text-center mb-3">Please enter the following fields to continue:</div>
						   <div class="text-center text-sm px-4 py-3 bg-gray-50 rounded-md mx-auto max-w-xs">
						     ${this.missingFields.map((field) => `<div class="py-1 border-b border-gray-200 last:border-0">${field}</div>`).join('')}
						   </div>`
						: 'Please enter all required fields to continue',
					confirmButtonColor: '#e59789',
				})
				return
			}

			this.updateAddress(JSON.parse(JSON.stringify(this.newAddress)))
		},
		buildAddressPayload() {
			return {
				...JSON.parse(JSON.stringify(this.newAddress)),
				isMilitary: this.isMilitaryAddress,
			}
		},
		syncShippingToStore() {
			if (this.suppressDebouncedSync) return

			this.syncPhoneFromDOM('shipping-phoneNumber')
			const validation = this.validateAddress()
			if (validation.missingFields) return

			const result = this.checkout.commitShippingAddress(this.buildAddressPayload())
			if (this.localBillingMatchesShipping && result.addressChanged) {
				this.checkout.copyShippingToBilling()
			}
			if (result.needsShippingRefetch) {
				this.$emit('shippingAddressSynced', {
					addressChanged: result.addressChanged,
					previousMethodId: result.previousMethodId,
					needsShippingRefetch: true,
				})
			}
		},
		copyStoreAddressToLocal() {
			devLog('copying store address to local')
			this.suppressDebouncedSync = true
			this.newAddress.address1 = this.checkout.shipping.shippingAddress.address1
			this.newAddress.address2 = this.checkout.shipping.shippingAddress.address2
			this.newAddress.company = this.checkout.shipping.shippingAddress.company
			this.newAddress.countryCode = this.checkout.shipping.shippingAddress.countryCode
			this.newAddress.firstName = this.checkout.shipping.shippingAddress.firstName
			this.newAddress.lastName = this.checkout.shipping.shippingAddress.lastName
			this.newAddress.phone = this.checkout.shipping.shippingAddress.phone
			this.newAddress.postalCode = this.checkout.shipping.shippingAddress.postalCode
			this.newAddress.hcProvider = this.checkout.shipping.shippingAddress.hcProvider
			this.newAddress.dueDate = this.checkout.shipping.shippingAddress.dueDate

			if (this.checkout.shipping.shippingAddress?.id) {
				this.newAddress.id = this.checkout.shipping.shippingAddress.id
			}

			// Detect military address - use isMilitary flag if available, otherwise check city/state
			const militaryCityCodes = ['APO', 'FPO', 'DPO']
			const militaryStateNames = ['Armed Forces Americas', 'Armed Forces Europe', 'Armed Forces Pacific']
			const militaryStateCodes = ['AA', 'AE', 'AP']

			// Map full state names to codes for the dropdown
			const stateNameToCode = {
				'Armed Forces Americas': 'AA',
				'Armed Forces Europe': 'AE',
				'Armed Forces Pacific': 'AP',
			}

			const stateValue = this.checkout.shipping.shippingAddress.stateOrProvince
			const cityValue = this.checkout.shipping.shippingAddress.city

			this.isMilitaryAddress =
				this.checkout.shipping.shippingAddress.isMilitary ||
				militaryCityCodes.includes(cityValue) ||
				militaryStateNames.includes(stateValue) ||
				militaryStateCodes.includes(stateValue)
			this.newAddress.isMilitary = this.isMilitaryAddress

			// Convert full state name to code if it's a military address
			let finalStateValue = stateValue
			if (this.isMilitaryAddress && stateNameToCode[stateValue]) {
				finalStateValue = stateNameToCode[stateValue]
			}

			// Set city and state in $nextTick after isMilitaryAddress watcher clears them
			this.$nextTick(() => {
				this.newAddress.city = cityValue
				this.newAddress.stateOrProvince = finalStateValue
			})

			this.initializeFormattedPhoneNumber()
			this.$nextTick(() => {
				this.suppressDebouncedSync = false
			})
		},
		applyTempFocusStyling(fieldNames) {
			// Clear any existing temporary focus fields
			this.tempFocusFields = []

			// Add the new fields to focus
			this.tempFocusFields = [...fieldNames]

			// Remove the focus after 1500ms
			setTimeout(() => {
				this.$nextTick(() => {
					this.tempFocusFields = []
				})
			}, 1200)
		},
		fetchCountries() {
			devLog('fetching countries')
			this.countries = Country.getAllCountries()
		},
		fetchStates() {
			if (this.newAddress.countryCode) {
				this.states = State.getStatesOfCountry(this.newAddress.countryCode)
			}
		},
		fetchStateName(stateCode, countryCode) {
			return State.getStateByCode(stateCode, countryCode).name
		},
		validateAddress() {
			devLog('validating new address...')
			const missingFields = []
			const fieldValidationStatus = []

			// Check validation status for each required field
			for (const field of this.required) {
				let isValid = true
				let displayName = this.getFieldDisplay(field)

				if (!this.newAddress[field]) {
					missingFields.push(displayName)
					isValid = false
				} else if (field === 'phone' && this.required.includes('phone')) {
					// Check if phone is required and has 10 digits
					const digitsOnly = this.newAddress.phone.replace(/\D/g, '')
					if (digitsOnly.length !== 10) {
						missingFields.push('Phone Number (must be 10 digits)')
						isValid = false
						displayName = 'Phone Number'
					}
				}

				// Add field validation status to the array
				fieldValidationStatus.push({
					name: displayName,
					field: field,
					validated: isValid,
				})
			}
			this.missingFields = missingFields

			if (missingFields.length > 0) {
				this.addressValidated = false
				devLog(`discovered missing fields in address ${missingFields.join(', ')}`)
				return { missingFields: true, message: `Please enter the following fields: ${missingFields.join(', ')}` }
			} else {
				this.addressValidated = true
				// devLog('address validated', this.newAddress)

				// this.updateAddress(JSON.parse(JSON.stringify(this.newAddress)))

				return { missingFields: false }
			}
		},
		getFieldDisplay(field) {
			const displayMap = {
				address1: 'Street Address',
				address2: 'Apt/Suite/Room',
				city: 'City',
				company: 'Company Name',
				firstName: 'First Name',
				lastName: 'Last Name',
				phone: 'Phone Number',
				postalCode: 'Postal Code',
				stateOrProvince: 'State/Province',
				countryCode: 'Country',
			}
			return displayMap[field] || field
		},
		updateAddress(address) {
			const payload = {
				...JSON.parse(JSON.stringify(address)),
				isMilitary: this.isMilitaryAddress,
			}
			const result = this.checkout.commitShippingAddress(payload)
			if (this.localBillingMatchesShipping) {
				this.checkout.copyShippingToBilling()
			}
			this.$emit('newAddressCompleted', {
				addressChanged: result.addressChanged,
				previousMethodId: result.previousMethodId,
				needsShippingRefetch: result.needsShippingRefetch,
			})
		},

		formatCountry(countryName) {
			const country = Country.getAllCountries().find((c) => c.name === countryName)
			return country ? country.isoCode : null
		},

		normalizePhoneDigits(value) {
			let digits = value.replace(/\D/g, '')
			if (digits.length === 11 && digits.startsWith('1')) {
				digits = digits.slice(1)
			}
			return digits.slice(0, 10)
		},
		syncPhoneFromDOM(inputId) {
			const phoneInput = document.getElementById(inputId)
			if (phoneInput && phoneInput.value && !this.newAddress.phone) {
				const digits = this.normalizePhoneDigits(phoneInput.value)
				this.newAddress.phone = digits
				this.formatDisplayPhone(digits)
			}
		},
		formatDisplayPhone(input) {
			let formatted = ''
			if (input.length <= 3) {
				formatted = input
			} else if (input.length <= 6) {
				formatted = `${input.slice(0, 3)}-${input.slice(3)}`
			} else {
				formatted = `${input.slice(0, 3)}-${input.slice(3, 6)}-${input.slice(6, 10)}`
			}
			this.formattedPhoneNumber = formatted
		},
		formatPhoneNumber(event) {
			const input = this.normalizePhoneDigits(event.target.value)

			// Store the raw digits in the newAddress.phone property
			this.newAddress.phone = input

			this.formatDisplayPhone(input)
		},
		initializeFormattedPhoneNumber() {
			if (this.newAddress && this.newAddress.phone) {
				const input = this.normalizePhoneDigits(this.newAddress.phone)
				this.newAddress.phone = input
				this.formatDisplayPhone(input)
			}
		},
		resetDueDate() {
			devLog('date value: ', this.newAddress.dueDate)
			// Use null instead of empty string for Safari compatibility
			this.newAddress.dueDate = null

			// Use $nextTick to ensure DOM updates properly
			this.$nextTick(() => {
				// Force a re-render of the input
				const dateInput = this.$el.querySelector('input[type="date"]')
				if (dateInput) {
					dateInput.value = ''
				}
			})
		},
		resetAddressForm() {
			this.newAddress = {
				address1: '',
				address2: '',
				city: '',
				company: '',
				countryCode: '',
				firstName: '',
				lastName: '',
				phone: '',
				postalCode: '',
				stateOrProvince: '',
				countryCode: 'US',
				hcProvider: false,
				dueDate: null,
				isMilitary: false,
			}
			this.isMilitaryAddress = false
			this.resetDueDate()
			this.formattedPhoneNumber = ''
		},
		copyShippingToBilling() {
			if (!this.checkout.shipping.shippingAddress) return
			const address = JSON.parse(JSON.stringify(this.checkout.shipping.shippingAddress))
			address.hcProvider = false
			address.dueDate = ''
			this.newAddress = address
		},

		initAutoComplete() {
			if (this.isMilitaryAddress || !this.enableAutoComplete || !isGoogleMapsReady()) return

			if (this.autocompleteInstance) return

			this.$nextTick(() => {
				const autocompleteId = 'shipping-autocomplete'
				const autocompleteElement = document.getElementById(autocompleteId)

				if (!autocompleteElement) {
					console.error(`Could not find element with id ${autocompleteId}`)
					return
				}

				this.autocompleteInstance = new google.maps.places.Autocomplete(autocompleteElement, { types: ['address'] })

				this.autocompleteInstance.addListener('place_changed', () => {
					const place = this.autocompleteInstance.getPlace()
					const parsed = parseGooglePlaceComponents(place?.address_components)
					if (!parsed) return

					this.newAddress.address1 = parsed.address1
					this.newAddress.city = parsed.city
					this.newAddress.stateOrProvince = parsed.stateOrProvince
					this.newAddress.postalCode = parsed.postalCode
					this.newAddress.countryCode = parsed.countryCode

					this.applyTempFocusStyling(['address1', 'city', 'stateOrProvince', 'postalCode', 'countryCode'])

					if (parsed.countryCode) {
						this.fetchStates()
					}

					this.debouncedSyncShippingToStore()
				})
			})
		},
	},

	watch: {
		newAddress: {
			handler() {
				if (this.suppressDebouncedSync) return
				this.debouncedSyncShippingToStore()
			},
			deep: true,
		},
		enableAutoComplete: {
			handler(newVal) {
				if (newVal && !this.isMilitaryAddress) {
					this.initAutoComplete()
				}
			},
			immediate: false,
		},
		showAddressForm: {
			handler(newVal) {
				if (newVal && this.checkout.shippingAddressValidated.value) {
					this.copyStoreAddressToLocal()
				}

				if (newVal) {
					this.localBillingMatchesShipping = this.checkout.shipping.billingMatchesShipping
				}
			},
		},
		saveNewAddress: {
			handler(newVal) {
				devLog('saveNewAddress', newVal)
				this.$emit('saveNewAddress', newVal)
			},
		},
		localBillingMatchesShipping: {
			handler(newVal) {
				this.checkout.updateBillingMatchesShipping(newVal)
			},
		},
		'newAddress.countryCode': {
			handler(newVal) {
				if (newVal !== 'US') {
					this.required = ['firstName', 'lastName', 'address1', 'city', 'countryCode', 'phone']
				} else {
					this.required = ['firstName', 'lastName', 'address1', 'city', 'stateOrProvince', 'postalCode', 'countryCode', 'phone']
				}
			},
		},
		isMilitaryAddress: {
			handler(newVal) {
				// Sync with newAddress.isMilitary
				this.newAddress.isMilitary = newVal

				if (newVal) {
					this.newAddress.countryCode = 'US'
					this.newAddress.city = ''
					this.newAddress.stateOrProvince = ''
					// Disable autocomplete
					if (this.autocompleteInstance) {
						google.maps.event.clearInstanceListeners(this.autocompleteInstance)
						this.autocompleteInstance = null
					}
					// Remove pac-container (autocomplete dropdown)
					const pacContainers = document.querySelectorAll('.pac-container')
					pacContainers.forEach((el) => el.remove())
				} else if (this.enableAutoComplete) {
					this.initAutoComplete()
				}
			},
		},
	},
}
</script>

<style scoped></style>
